import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TableComponents } from 'react-virtuoso';
import { TableVirtuoso } from 'react-virtuoso';
import styled from '@emotion/styled';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import { Label } from '@components/Label';
import { IconButton } from '@components/buttons';
import { EmptyRow, SkeletonRows, TableHeaderCell } from '@components/table';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { PencilIcon } from '@icons/PencilIcon';
import { TrashIcon } from '@icons/TrashIcon';
import type { SubscriptionRequestHistoryDeleteRq } from '@repositories/subscriptionRequestRepository/subscriptionRequest.types';
import { useDeleteSubscriptionRequestHistoryMutation } from '@queryHooks/useSubscriptionRequest';
import type { SubscriptionRequestHistoryDTOModel } from '@repositories/subscriptionRequestRepository/models/SubscriptionRequestHistoryDTOModel';
import type { SubscriptionRequestModel } from '@repositories/subscriptionRequestRepository/models/SubscriptionRequestModel';
import { SubscriptionRequestHistoryUpdateModal } from './SubscriptionRequestHistoryUpdateModal';

type TableContext = {
  isLoading: boolean;
  emptyDataMessage: string;
  onRowClick: (item: any) => void;
};

type Props = {
  subscriptionRequest: SubscriptionRequestModel;
  isSubscriptionRequestLoading: boolean;
  scrollTarget: HTMLElement | null;
};

const StyledTableRow = styled(TableRow)`
  cursor: pointer;

  &:hover td {
    background-color: ${({ theme }) => theme.colors['state-white-primary-hover']};
  }
`;

const StyledTableHead = styled(TableHead)`
  background-color: ${({ theme }) => theme.colors['bg-white']};
`;

const historyTableComponents: TableComponents<SubscriptionRequestModel, TableContext> = {
  Table,
  TableHead: StyledTableHead as any,
  TableRow: ({ children, item, context, ...props }) => (
    <StyledTableRow
      key={nanoid()}
      {...props}
      onClick={() => {
        if (typeof context?.onRowClick === 'function') {
          context.onRowClick(item);
        }
      }}
    >
      {children}
    </StyledTableRow>
  ),
  TableBody: TableBody as any,
  EmptyPlaceholder: ({ context }) =>
    context ? (
      <TableBody>
        {context.isLoading ? (
          <SkeletonRows rowNum={3} colNum={6} />
        ) : (
          <EmptyRow colSpan={6} title={context.emptyDataMessage} />
        )}
      </TableBody>
    ) : null,
};
export const HistoryRequestTable = (props: Props) => {
  const { t } = useTranslation();
  const { subscriptionRequest, isSubscriptionRequestLoading, scrollTarget } = props;
  const {
    uiStore: { alertStore, toastStore },
  } = useStore();

  // 처리내역 수정 모달 오픈 여부
  const [isOpenSubscriptionRequestHistoryUpdateModal, setIsOpenSubscriptionRequestHistoryUpdateModal] =
    useState<boolean>(false);

  const [selectedHistory, setSelectedHistory] = useState<SubscriptionRequestHistoryDTOModel | null>(null);

  const { mutate: updateSubscriptionRequestHistory } = useDeleteSubscriptionRequestHistoryMutation();

  //  처리내역 삭제 버튼
  const handleClickSubscriptionRequestHistoryDeleteBtn = useCallback(
    (history: SubscriptionRequestHistoryDTOModel) => {
      alertStore.open({
        title: t('Purchase_History.Delete_Request'),
        message: t('Purchase_History.Delete_Request_Message'),
        useCancel: true,
        confirmName: t('History.Delete'),
        type: 'error',
        onConfirm: () => {
          if (!history?.id) {
            toastStore.open(t('Purchase_History.Delete_Request_Failed'));
            console.error('no id');
            return;
          }

          const rqData: SubscriptionRequestHistoryDeleteRq = {
            subscriptionRequestId: subscriptionRequest.id,
            subscriptionRequestHistoryId: history.id,
          };
          updateSubscriptionRequestHistory(rqData, {
            onSuccess: () => {
              toastStore.open(t('Purchase_History.Delete_Request_Complete'));
            },
            onError: (error: unknown) => {
              toastStore.open(t('Purchase_History.Delete_Request_Failed'));
              console.error(error);
            },
          });
        },
      });
    },
    [alertStore, subscriptionRequest.id, t, toastStore, updateSubscriptionRequestHistory],
  );

  // 처리내역 수정 버튼 클릭
  const handleClickSubscriptionRequestHistoryBtn = useCallback(
    (subscriptionRequestHistoryId: string, mode: 'UPDATE' | 'DELETE') => {
      const selectedHistory = subscriptionRequest.histories.find(
        history => history.id === subscriptionRequestHistoryId,
      );
      if (selectedHistory) {
        setSelectedHistory(selectedHistory);
        if (mode === 'UPDATE') {
          handleToggleSubscriptionRequestHistoryUpdateModal(true);
        } else {
          handleClickSubscriptionRequestHistoryDeleteBtn(selectedHistory);
        }
      }
    },
    [handleClickSubscriptionRequestHistoryDeleteBtn, subscriptionRequest.histories],
  );

  const handleClearSelectedHistory = () => {
    setSelectedHistory(null);
  };

  // 처리내역 수정 모달 오픈 토글
  const handleToggleSubscriptionRequestHistoryUpdateModal = (isOpen: boolean = false) => {
    setIsOpenSubscriptionRequestHistoryUpdateModal(isOpen);
  };

  const renderHistoryTableHeader = useCallback(
    () => (
      <TableRow>
        <TableHeaderCell align="center" width={95}>
          {t('HistoryRequestTable.Number')}
        </TableHeaderCell>
        <TableHeaderCell width={200}>{t('HistoryRequestTable.Written_Date')}</TableHeaderCell>
        <TableHeaderCell width={200}>{t('HistoryRequestTable.Written_By')}</TableHeaderCell>
        <TableHeaderCell width={590}>{t('Purchase_History.License_Issuance_Content')}</TableHeaderCell>
        <TableHeaderCell width={110}>{t('HistoryRequestTable.Processing_Status')}</TableHeaderCell>
        <TableHeaderCell width={88}>{t('HistoryRequestTable.Function')}</TableHeaderCell>
      </TableRow>
    ),
    [t],
  );

  const renderHistoryTableItem = useCallback(
    (index: number, history: any) => {
      const { status, managerName, createdDate, comment, id } = history;
      const {
        inquiry,
        user: { name },
      } = subscriptionRequest;

      return (
        <>
          <HistoryTableCell align="center" width={95}>
            {index + 1}
          </HistoryTableCell>
          <HistoryTableCell width={200}>{dayjs(createdDate).format(t('DateFormat_YMD'))}</HistoryTableCell>
          <HistoryTableCell width={200}>
            {/*
            @description: 처리내역 작성자 출력 ( 최초 REQUESTED 상태인 경우 managerName, comment 안내려옴  * FE에서 처리하는 것으로  BE와 합의 완료 )
            REQUESTED 상태인 경우 user.name, inquery 출력
            REQUESTED 상태가 아닌 경우 managerName, comment 출력
        */}
            {status === 'REQUESTED' ? name : managerName || '-'}
          </HistoryTableCell>
          <HistoryTableCell width={590}>{status === 'REQUESTED' ? inquiry : comment || '-'}</HistoryTableCell>
          <HistoryTableCell width={110}>
            <Label variant="contain" color={subscriptionRequest.getStatusLabelColor(status)}>
              {subscriptionRequest.getStatusStr(status)}
            </Label>
          </HistoryTableCell>
          <HistoryTableCell width={88}>
            {index === 0 && (status === 'APPROVED' || status === 'REJECTED') && (
              <IconContainer>
                <IconButton
                  variant="outline"
                  size="extraSmall"
                  onClick={() => handleClickSubscriptionRequestHistoryBtn(id, 'UPDATE')}
                >
                  <PencilIcon />
                </IconButton>
                <IconButton
                  variant="outline"
                  size="extraSmall"
                  onClick={() => handleClickSubscriptionRequestHistoryBtn(id, 'DELETE')}
                >
                  <TrashIcon />
                </IconButton>
              </IconContainer>
            )}
          </HistoryTableCell>
        </>
      );
    },
    [handleClickSubscriptionRequestHistoryBtn, subscriptionRequest, t],
  );

  return (
    <TableWrapper>
      <TableVirtuoso
        data={subscriptionRequest.getHistories(false)}
        components={historyTableComponents}
        fixedHeaderContent={renderHistoryTableHeader}
        itemContent={renderHistoryTableItem}
        computeItemKey={(_, item) => item?.id}
        context={{
          isLoading: isSubscriptionRequestLoading,
          emptyDataMessage: t('Subscrib_Main_Filter_08'),
          onRowClick: () => {},
        }}
        customScrollParent={scrollTarget ?? undefined}
      />
      {/* 처리내역 수정 모달 */}
      {selectedHistory && isOpenSubscriptionRequestHistoryUpdateModal && (
        <SubscriptionRequestHistoryUpdateModal
          isOpen={isOpenSubscriptionRequestHistoryUpdateModal}
          selectedHistory={selectedHistory}
          toggleModal={handleToggleSubscriptionRequestHistoryUpdateModal}
          clearSelectedHistory={handleClearSelectedHistory}
        />
      )}
    </TableWrapper>
  );
};

const TableWrapper = styled.div`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.02), 0px 0px 3px 0px rgba(0, 0, 0, 0.07);
  margin: 13px 0px;
`;

const HistoryTableCell = styled(TableCell)`
  padding: 16px 10px;
  ${fonts.Headline8};
  color: ${({ theme }) => theme.colors['text-gray-main']};
  background-color: ${({ theme }) => theme.colors['bg-white']};
  border-bottom: 1px solid ${({ theme }) => theme.colors['border-gray-w-lighter']};
  border-right: 1px solid ${({ theme }) => theme.colors['border-gray-w-lighter']};

  white-space: pre-line;
  &:last-of-type {
    border-right: none;
  }
`;

const IconContainer = styled.div`
  display: flex;
  gap: 8px;
`;
